/**
 * Shared utilities for {@link AssessmentRow}, {@link AssessmentCard} and {@link AssessmentsDataTable}.
 */
import { getErrorMessage } from '@/utils/errors-utils';
import { ASSESSMENT_TYPE_DEFINITIONS } from './assessment-configs';
import type {
  Assessment,
  AssessmentFilters,
  AssessmentType,
  GetAllAssessmentsParams,
} from '@/types/assessment.types';

/** Disabled flag + tooltip for one row action. */
interface AssessmentActionState {
  disabled: boolean;
  /** Always set: an enabled button describes the action, a disabled one describes the blocker. */
  tooltip: string;
}

/** Interface-element grants for one row, before the per-type and per-status rules apply. */
interface AssessmentActionGrants {
  edit: boolean;
  validate: boolean;
  export: boolean;
  delete: boolean;
}

/** Edit / validate / export / delete states for one row. */
export interface AssessmentRowActions {
  edit: AssessmentActionState;
  validate: AssessmentActionState;
  export: AssessmentActionState;
  delete: AssessmentActionState;
}

/** Assessment fields needed to resolve row-action enablement. */
type AssessmentActionTarget = Pick<Assessment, 'status' | 'type'>;

/** Display name for an assessment target user. */
export function formatAssessmentUserName(
  user:
    | {
        displayName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
): string {
  if (!user) return '—';
  return (
    user.displayName?.trim() ||
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    user.email ||
    '—'
  );
}

/** Format an assessment datetime for FR display. */
export function formatAssessmentDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format an assessment date for FR display. */
export function formatAssessmentDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Mirrors {@link ROLE_CODES_BY_RANK} server-side. */
const ADMIN_MANAGER_ROLE_CODES = ['admin', 'manager', 'manager_remote'];

/** Coordinator / recruiter codes including historical misspellings the API still emits. */
const COORDINATOR_ROLE_CODES = [
  'coordinator',
  'coordinateur',
  'cordinateur',
  'coordinator_remote',
  'recruiter',
  'recruteur',
  'recruiter_remote',
];

/** Role codes at or above coordinator — they may fill any type in. */
const ABOVE_VOLUNTEER_ROLE_CODES = [...ADMIN_MANAGER_ROLE_CODES, ...COORDINATOR_ROLE_CODES];

/** Case-insensitive intersection of the user's role codes with `expected`. */
function hasAnyRole(roleCodes: string[], expected: string[]): boolean {
  return roleCodes.some((code) => expected.includes(code.toLowerCase()));
}

/**
 * Mirrors {@link BilanPermissionsService}: a VSC only writes types whose writer is volunteer;
 * coordinator-authored types stay read-only for a VSC.
 */
export function canRoleWriteAssessmentType(roleCodes: string[], type: AssessmentType): boolean {
  if (hasAnyRole(roleCodes, ABOVE_VOLUNTEER_ROLE_CODES)) return true;
  return ASSESSMENT_TYPE_DEFINITIONS[type].writtenBy !== 'coordinator';
}

/**
 * Mirrors {@link BilanPermissionsService}: validation belongs to the tier above the designated writer.
 * Coordinators validate volunteer-written types; coordinator-authored types go up to a Manager.
 */
export function canRoleValidateAssessmentType(roleCodes: string[], type: AssessmentType): boolean {
  if (hasAnyRole(roleCodes, ADMIN_MANAGER_ROLE_CODES)) return true;
  if (!hasAnyRole(roleCodes, COORDINATOR_ROLE_CODES)) return false;
  return ASSESSMENT_TYPE_DEFINITIONS[type].writtenBy !== 'coordinator';
}

/** Edit: grant + writer-tier + not `validated`. */
function resolveEditAction(
  assessment: AssessmentActionTarget,
  granted: boolean,
  roleCodes: string[],
): AssessmentActionState {
  if (!granted) return { disabled: true, tooltip: getErrorMessage('cannotCompleteAssessment') };
  if (!canRoleWriteAssessmentType(roleCodes, assessment.type)) {
    return { disabled: true, tooltip: getErrorMessage('assessmentWrittenByCoordinator') };
  }
  if (assessment.status === 'validated') {
    return { disabled: true, tooltip: getErrorMessage('validatedAssessmentLocked') };
  }
  return { disabled: false, tooltip: 'Compléter le bilan' };
}

/** Validate: grant + validator-tier + status `submitted`. */
function resolveValidateAction(
  assessment: AssessmentActionTarget,
  granted: boolean,
  roleCodes: string[],
): AssessmentActionState {
  if (!granted) return { disabled: true, tooltip: getErrorMessage('cannotValidateAssessment') };
  if (!canRoleValidateAssessmentType(roleCodes, assessment.type)) {
    return {
      disabled: true,
      tooltip: getErrorMessage('assessmentValidateRoleRestricted'),
    };
  }
  if (assessment.status === 'validated')
    return { disabled: true, tooltip: getErrorMessage('assessmentAlreadyValidated') };
  if (assessment.status !== 'submitted') {
    return { disabled: true, tooltip: getErrorMessage('assessmentMustBeSubmittedToValidate') };
  }
  return { disabled: false, tooltip: 'Valider le bilan' };
}

/** Export PDF is only enabled after `validated`. */
function resolveExportAction(
  assessment: AssessmentActionTarget,
  granted: boolean,
): AssessmentActionState {
  if (!granted) return { disabled: true, tooltip: getErrorMessage('cannotExportAssessment') };
  if (assessment.status !== 'validated') {
    return {
      disabled: true,
      tooltip: getErrorMessage('assessmentExportAfterValidation'),
    };
  }
  return { disabled: false, tooltip: 'Exporter le bilan' };
}

/** Delete is grant-only (no extra status rule). */
function allowedDeleteAction(): AssessmentActionState {
  return { disabled: false, tooltip: 'Supprimer le bilan' };
}

/** Delete denied: missing interface-element grant. */
function deniedDeleteAction(): AssessmentActionState {
  return { disabled: true, tooltip: getErrorMessage('cannotDeleteAssessment') };
}

/**
 * Row/card actions: other roles keep the full set (unused ones disabled + tooltip).
 * In-person Recruiter is consult-only — only the view (eye) icon is rendered.
 */
export function resolveAssessmentRowActions(
  assessment: AssessmentActionTarget,
  grants: AssessmentActionGrants,
  roleCodes: string[],
): AssessmentRowActions {
  return {
    edit: resolveEditAction(assessment, grants.edit, roleCodes),
    validate: resolveValidateAction(assessment, grants.validate, roleCodes),
    export: resolveExportAction(assessment, grants.export),
    delete: grants.delete ? allowedDeleteAction() : deniedDeleteAction(),
  };
}

/** List GET params from table filters + pagination. */
export function buildListParams(
  filters: AssessmentFilters,
  options: { page: number; pageSize: number },
): GetAllAssessmentsParams {
  return {
    page: options.page,
    pageSize: options.pageSize,
    type: filters.type,
    status: filters.status,
    establishmentId: filters.establishmentId,
    search: filters.search,
  };
}

/**
 * Resolves the display name of the subject of an assessment:
 * targetUser → coordinatorUser → "—".
 */
export function deriveAssessmentSubjectName(assessment: Assessment): string {
  if (assessment.targetUser) return formatAssessmentUserName(assessment.targetUser);
  if (assessment.coordinatorUser) return formatAssessmentUserName(assessment.coordinatorUser);
  return '—';
}
